import { TestResponse, RecruitmentStatus } from "@/types/test-response";
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Pencil } from "lucide-react";
import { format } from "date-fns";
import { StatusDisplay } from "./StatusDisplay";
import { ScoreDisplay } from "./ScoreDisplay";
import { IntegrityDisplay } from "./IntegrityDisplay";
import { RecruitmentStatusSelect } from "./RecruitmentStatusSelect";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from "@/components/ui/context-menu";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useRecruitmentStatus } from "@/hooks/useRecruitmentStatus";
import { RecruitmentNotesDialog } from "./RecruitmentNotesDialog";

interface MobileResponseCardProps {
  response: TestResponse;
  canManageRecruitment?: boolean;
  statusColorsEnabled?: boolean;
}

export function MobileResponseCard({ 
  response, 
  canManageRecruitment = false,
  statusColorsEnabled = false
}: MobileResponseCardProps) {
  const navigate = useNavigate();
  const [notesDialogOpen, setNotesDialogOpen] = useState(false);
  const [currentStatus, setCurrentStatus] = useState<RecruitmentStatus>(
    response.recruitment_status || 'pending'
  );
  
  const { updateRecruitmentStatus, isUpdating } = useRecruitmentStatus();
  
  // Keep local state in sync with prop updates
  useEffect(() => {
    setCurrentStatus(response.recruitment_status || 'pending');
  }, [response.recruitment_status]);

  const timeTaken = response.completed_at
    ? Math.round(
        (new Date(response.completed_at).getTime() -
          new Date(response.started_at).getTime()) /
          (1000 * 60)
      )
    : null;

  const totalMarks = response.test.questions?.reduce(
    (sum: number, q: any) => sum + (q.marks || 1),
    0
  ) || 0;

  const aiScore = response.total_ai_score || 0;

  const confirmedScore = response.question_responses.reduce(
    (sum: number, qr: any) => sum + (qr.score || 0),
    0
  );
  
  const responseDate = format(new Date(response.started_at), "MMM d, yyyy");
  
  // Handle opening in new tab
  const handleOpenInNewTab = () => {
    window.open(`/test-response/${response.id}`, '_blank');
  };
  
  // Handle status change locally first, then propagate
  const handleStatusChange = async (status: RecruitmentStatus) => {
    setCurrentStatus(status); // Update local state immediately
    try {
      await updateRecruitmentStatus.mutateAsync({
        responseId: response.id,
        status,
        notes: response.recruitment_notes || undefined,
      });
    } catch (error) {
      // Revert local state on error
      setCurrentStatus(response.recruitment_status || 'pending');
    }
  };

  // Get background color based on recruitment status
  const getCardBackgroundClass = () => {
    if (!statusColorsEnabled || response.status !== 'completed') {
      return '';
    }
    
    if (currentStatus === 'shortlisted') {
      return 'bg-blue-50 dark:bg-blue-950/20 border-blue-200 dark:border-blue-800';
    }
    if (currentStatus === 'hired') {
      return 'bg-green-50 dark:bg-green-950/20 border-green-200 dark:border-green-800';
    }
    if (currentStatus === 'rejected') {
      return 'bg-red-50 dark:bg-red-950/20 border-red-200 dark:border-red-800';
    }
    return '';
  };

  const handleNavigate = () => {
    navigate(`/test-response/${response.id}`);
  };

  const handleOpenNotesDialog = () => {
    setNotesDialogOpen(true);
  };
  
  return (
    <>
      <ContextMenu>
        <ContextMenuTrigger asChild>
          <Card key={response.id} className={`mb-4 cursor-pointer ${getCardBackgroundClass()}`} onClick={handleNavigate}>
            <CardContent className="pt-4 pb-2">
              <div className="flex justify-between items-start mb-3">
                <div className="space-y-1">
                  <h3 className="font-medium">{response.candidate.full_name}</h3>
                </div>
                <StatusDisplay status={response.status} />
              </div>

              <div className="grid grid-cols-2 gap-2 text-sm my-3">
                <div className="flex flex-col">
                  <span className="text-xs text-muted-foreground">Date</span>
                  <span>{responseDate}</span>
                </div>

                <div className="flex flex-col">
                  <span className="text-xs text-muted-foreground">Time Taken</span>
                  <span>{timeTaken ? `${timeTaken} mins` : "-"}</span>
                </div>

                <div className="flex flex-col">
                  <span className="text-xs text-muted-foreground">Status</span>
                  <div className="flex items-center mt-1">
                    {response.status === "completed" && (
                      <Badge variant="outline" className="mr-2">
                        {currentStatus}
                      </Badge>
                    )}
                  </div>
                </div>

                <div className="flex flex-col">
                  <span className="text-xs text-muted-foreground">AI Score</span>
                  {response.status === "completed" ? (
                    <ScoreDisplay score={aiScore} total={totalMarks} />
                  ) : (
                    <span>-</span>
                  )}
                </div>

                <div className="flex flex-col">
                  <span className="text-xs text-muted-foreground">Final Score</span>
                  {response.scores_confirmed ? (
                    <ScoreDisplay score={confirmedScore} total={totalMarks} />
                  ) : (
                    <span className="text-muted-foreground text-sm italic">Not marked</span>
                  )}
                </div>
              </div>

              {response.status === "completed" && (
                <div className="mt-3 pt-3 border-t flex flex-col">
                  <span className="text-xs text-muted-foreground mb-1">Integrity</span>
                  <IntegrityDisplay score={response.integrity_score || 0} />
                </div>
              )}
            </CardContent>

            {response.status === "completed" && (
              <CardFooter className="flex flex-col gap-2 pt-2 pb-3" onClick={(e) => e.stopPropagation()}>
                {canManageRecruitment && (
                  <div className="flex items-center gap-2 w-full mb-1">
                    <RecruitmentStatusSelect
                      value={currentStatus}
                      onChange={handleStatusChange}
                      disabled={isUpdating}
                      className="flex-1"
                    />
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      className="h-8 w-8" 
                      onClick={handleOpenNotesDialog}
                      disabled={isUpdating}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                  </div>
                )}

                <Button
                  size="sm"
                  onClick={handleNavigate}
                  className="w-full"
                >
                  View Result
                </Button>
              </CardFooter>
            )}
          </Card>
        </ContextMenuTrigger>
        <ContextMenuContent className="w-48">
          <ContextMenuItem onClick={handleOpenInNewTab}>Open in new tab</ContextMenuItem>
        </ContextMenuContent>
      </ContextMenu>

      {response.status === "completed" && (
        <RecruitmentNotesDialog
          open={notesDialogOpen}
          onOpenChange={setNotesDialogOpen}
          response={response}
        />
      )}
    </>
  );
}
